import { Context, createContext, FC, useContext } from 'react';
import {
  AuditLogsApi,
  AzureSubscriptionsApi,
  ReportsApi,
  ServiceModulesApi,
  ServiceNowApi,
  ServiceRequestsApi,
  TermsConditionsApi,
  PublisherUpdatesApi,
  MyProjectsApi,
  NotificationsApi,
  LocaleApi,
  UsersApi,
  SSRMRequestsApi,
} from 'cmp-client-api';
import { AuditLogsService } from '@src/server/services/audit-logs.service';
import { LogoutEventService } from '@src/server/services/logout-event.service';
import { AzureSubscriptionService } from '@src/server/services/azure-subsription.service';
import { ServiceModuleService } from '@src/server/services/service-module.service';
import { SupportSystemService } from '@src/server/services/support-system.service';
import { ServiceRequestService } from '@src/server/services/service-request.service';
import { AuditLogsMapper } from '@src/server/converters/audit-logs.mapper';
import { UserPolicyService } from '@src/server/services/user-policy.service';
import { PublisherUpdatesService } from '@src/server/services/publisher-updates.service';
import { MyProjectsService } from '@src/server/services/my-projects.service';
import { NotificationService } from '@src/server/services/notification.service';
import {
  AzureSubscriptionMapper,
  AzureSubscriptionQuickLaunchMapper,
  BrownfieldProvisioningMapper,
  BrownfieldProvisioningSubnetMapper,
  BrownfieldProvisioningVirtualNetworkMapper,
} from '@src/server/converters/azure-subscription.mapper';
import { ServiceModuleMapper } from '@src/server/converters/service-module.mapper';
import { ServiceRequestAndAzureSubscriptionContextMapper } from '@src/server/converters/service-request.mapper';
import { ReportRequestMapper } from '@src/server/converters/report-request.mapper';
import type {
  AuditLogsServiceInterface,
  AzureSubscriptionServiceInterface,
  ReportsServiceInterface,
  ServiceModuleServiceInterface,
  ServiceRequestServiceInterface,
  LogoutEventServiceInterface,
  SupportSystemServiceInterface,
  HelpServiceInterface,
  RequestsServiceInterface,
} from '@src/server/index';
import type { ProviderPropsInterface } from '@src/core/auth/components/types/provider-props.interface';
import type { ServiceRegistryProviderPropsInterface } from '@src/server/types/service-registry-provider-props.interface';
import { HelpRequestMapper } from '@src/server/converters/help-module.mapper';
import { UserPolicyServiceInterface } from '@src/server/services/types/user-policy.service.interface';
import { Octokit } from '@octokit/rest';
import { PublisherUpdatesServiceInterface } from '@src/server/services/types/publisher-updates-service.interface';
import {
  PagedPublisherContractsMapper,
  PagedPublisherUpdatesMapper,
  PublisherUpdatesMapper,
} from '@src/server/converters/publisher-updates.mapper';
import { GithubService } from './services/github.service';
import { HelpService } from './services/help.service';
import { ReportsService } from './services/reports.service';
import { GithubServiceInterface } from './services/types/github.service.interface';
import { SupportSystemTicketMapper } from './converters/support-system.mapper';
import { MyProjectsServiceInterface } from './services/types/my-projects-service.interface';
import { MyProjectMapper } from './converters/my-projects.mapper';
import { CaseMapper } from './converters/case.mapper';
import { NotificationServiceInterface } from './services/types/notification.service.interface';
import { LocaleService } from './services/locale.service';
import { UserService } from './services/user.service';
import { RequestsService } from './services/requests.service';

interface InjectableServices {
  ServiceModuleService?: ServiceModuleServiceInterface;
  ServiceRequestService?: ServiceRequestServiceInterface;
  ReportsService?: ReportsServiceInterface;
  HelpService?: HelpServiceInterface;
  AzureSubscriptionService?: AzureSubscriptionServiceInterface;
  AuditLogsService?: AuditLogsServiceInterface;
  LogoutEventService?: LogoutEventServiceInterface;
  SupportSystemService?: SupportSystemServiceInterface;
  UserPolicyService?: UserPolicyServiceInterface;
  PublisherUpdatesService?: PublisherUpdatesServiceInterface;
  GithubService?: GithubServiceInterface;
  MyProjectsService?: MyProjectsServiceInterface;
  NotificationService?: NotificationServiceInterface;
  LocaleService?: LocaleService;
  UserService?: UserService;
  RequestsService?: RequestsServiceInterface;
}

export const ServiceRegistryContext: Context<InjectableServices | null> = createContext(null);

export const ServiceRegistryProvider: FC<
  ServiceRegistryProviderPropsInterface & ProviderPropsInterface
> = (props) => {
  const { children, cmpHttpClient } = props;

  const serviceRequestApi = new ServiceRequestsApi(cmpHttpClient);
  const serviceModulesApi = new ServiceModulesApi(cmpHttpClient);
  const azureSubscriptionsApi = new AzureSubscriptionsApi(cmpHttpClient);

  const services: InjectableServices = {
    ServiceModuleService: new ServiceModuleService(serviceModulesApi, ServiceModuleMapper),
    ServiceRequestService: new ServiceRequestService(
      serviceRequestApi,
      ServiceRequestAndAzureSubscriptionContextMapper
    ),
    SupportSystemService: new SupportSystemService(
      new ServiceNowApi(cmpHttpClient),
      SupportSystemTicketMapper,
      CaseMapper
    ),
    ReportsService: new ReportsService(
      new ReportsApi(cmpHttpClient),
      ReportRequestMapper,
      serviceRequestApi
    ),
    HelpService: new HelpService(HelpRequestMapper, serviceRequestApi),
    AzureSubscriptionService: new AzureSubscriptionService(
      azureSubscriptionsApi,
      AzureSubscriptionMapper,
      AzureSubscriptionQuickLaunchMapper,
      BrownfieldProvisioningMapper,
      BrownfieldProvisioningVirtualNetworkMapper,
      BrownfieldProvisioningSubnetMapper
    ),
    AuditLogsService: new AuditLogsService(new AuditLogsApi(cmpHttpClient), AuditLogsMapper),
    LogoutEventService: new LogoutEventService(new AuditLogsApi(cmpHttpClient)),
    UserPolicyService: new UserPolicyService(new TermsConditionsApi(cmpHttpClient)),
    PublisherUpdatesService: new PublisherUpdatesService(
      new PublisherUpdatesApi(cmpHttpClient),
      PublisherUpdatesMapper,
      PagedPublisherUpdatesMapper,
      PagedPublisherContractsMapper
    ),
    GithubService: new GithubService(new Octokit({}), serviceModulesApi),
    MyProjectsService: new MyProjectsService(new MyProjectsApi(cmpHttpClient), MyProjectMapper),
    NotificationService: new NotificationService(new NotificationsApi(cmpHttpClient)),
    LocaleService: new LocaleService(new LocaleApi(cmpHttpClient)),
    UserService: new UserService(new UsersApi(cmpHttpClient)),
    RequestsService: new RequestsService(new SSRMRequestsApi(cmpHttpClient)),
  };

  return (
    <ServiceRegistryContext.Provider value={services}>
      {children && children}
    </ServiceRegistryContext.Provider>
  );
};

export const useInjectableServices = (): InjectableServices => {
  const injectableServices: InjectableServices = useContext(ServiceRegistryContext);

  if (!injectableServices) {
    throw new Error(`No services provided! Please provide your own services`);
  }
  return injectableServices;
};
